import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Table,
  CisContainer,
  Button,
  Row,
  Col,
  ButtonSearch,
  Pagination,
  FiltersControls,
  Spinner,
  Modal,
} from 'components';
import { useState } from 'react';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { globalPropTypes } from 'helpers';
import moment from 'moment';
import { fetchPdfCisReports } from '../../../api/services/cis';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import checkPermissions from '../../../helpers/checkPermissions';

const sortByConfig = {
  name: 'sortBy',
  options: [
    { label: 'Year: descending', value: 'year.desc' },
    { label: 'Year: ascending', value: 'year.asc' },
    { label: 'Date: descending', value: 'date.desc' },
    { label: 'Date: ascending', value: 'date.asc' },
  ],
};

const CertificationReports = ({
  loading,
  type,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setProfile,
  createReport,
  certificationBody,
  setFilters,
  userPermissions,
}) => {
  const { items, page, pages, perPage, sort, totalItems, filters } = list;

  const [portalReady, setPortalReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    setPage(1);
    setPerPage(10);
    setSort('year.desc');
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  useEffect(() => {
    if (document.getElementById(`${type}-cis-buttons-portal`)) {
      setPortalReady(true);
    }
  }, []);

  // Determine if previous year report exists
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;
  const previousYearReportExists = items.some((item) => item.year === previousYear);

  const manageCisPermission =
    type === 'ipma' || checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions || []);

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.year || ''}
                  onFilter={(val) => handleColumnFilterChange('year', val)}
                  filterPlaceholder={t('page.user.cis.certificationReports.yearPlaceholder')}
                  label={t('page.user.cis.certificationReports.year')}
                  sort
                  sortDirection={getSortDirection('year')}
                  onSort={(dir) => handleColumnSort('year', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.statusManual || ''}
                  onFilter={(val) => handleColumnFilterChange('statusManual', val)}
                  filterOptions={[
                    { label: 'Draft', value: 'Draft' },
                    { label: 'Reviewed', value: 'Reviewed' },
                    { label: 'Accepted', value: 'Accepted' },
                  ]}
                  filterPlaceholder={t('page.user.cis.certificationReports.statusPlaceholder')}
                  label={t('page.user.cis.certificationReports.status')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFrom || ''}
                  filterValueTo={filters.dateTo || ''}
                  filterValueYear={filters.dateYear || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateTo', val)}
                  onFilterYear={(val) => handleColumnFilterChange('dateYear', val)}
                  filterPlaceholder={t('page.user.cis.certificationReports.datePlaceholder')}
                  label={t('page.user.cis.certificationReports.date')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                {t('page.user.cis.certificationReports.exportToPdf')}
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                {t('page.user.cis.certificationReports.viewEdit')}
              </CisTable.StickyHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={6}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={6}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.year')}</Table.Label>
                      {item.year}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.status')}</Table.Label>
                      {item.status}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.certificationReports.date')}</Table.Label>
                      {moment(item.dateOfReport).format('DD.MM.YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.user.cis.certificationReports.exportToPdf')}
                      </Table.Label>
                      <ButtonSearch
                        icon="exportToPdf"
                        color="black"
                        secondary
                        rounded
                        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        onClick={() => {
                          fetchPdfCisReports(item.id);
                        }}
                      />
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <CisTable.Label>
                        {t('page.user.cis.certificationReports.viewEdit')}
                      </CisTable.Label>
                      {item.status.toUpperCase() === 'ACCEPTED' ? (
                        <ButtonSearch
                          icon="searchIcon"
                          onClick={() => {
                            setProfile(item);
                            history.push('/ma/cis/reports/view');
                          }}
                          secondary
                          rounded
                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        />
                      ) : item.status.toUpperCase() === 'DRAFT' ||
                        item.status.toUpperCase() === 'REVIEWED' ? (
                        <ButtonSearch
                          icon={manageCisPermission ? 'edit' : 'searchIcon'}
                          color="black"
                          onClick={() => {
                            setProfile(item);

                            if (!manageCisPermission) {
                              history.push('/ma/cis/reports/view');
                            } else {
                              history.push('/ma/cis/reports/edit');
                            }
                          }}
                          secondary
                          rounded
                          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        />
                      ) : null}
                    </CisTable.Cell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </CisContainer>
      <Modal
        isOpen={isModalOpen}
        confirm={() => {
          const a = {
            status: 'Draft',
            certificationBody: certificationBody,
            dateOfReport: moment().format('YYYY-MM-DD'),
          };

          createReport(a, history);
          setIsModalOpen(false);
        }}
        cancel={() => setIsModalOpen(false)}
        onRequestClose={() => setIsModalOpen(false)}
        heading={t('page.user.cis.certificationReports.confirmCreation')}
      ></Modal>
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Row style={{ alignItems: 'center', justifyContent: 'end' }}>
            <Col size={{ xs: 1 / 3 }}>
              <Button
                secondary
                onClick={() => setIsModalOpen(true)}
                disabled={previousYearReportExists}
              >
                {previousYearReportExists
                  ? t('page.user.cis.certificationReports.previousYearExists')
                  : t('page.user.cis.certificationReports.new')}
              </Button>
            </Col>
          </Row>,
          document.getElementById(`${type}-cis-buttons-portal`),
        )}
    </PageContent>
  );
};

CertificationReports.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['ipma', 'ma']),
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
};

CertificationReports.defaultProps = {
  loading: false,
  type: 'ma',
};

export default CertificationReports;
